"use client"

import { useEffect } from "react"
import { useRouter, usePathname, useSearchParams } from "next/navigation"

interface MortgageFilterPreferencesProps {
  state: string
  borrowerType: string
  interestOnly: boolean
  lvr: string
  rateDisplayType: string
}

export default function MortgageFilterPreferences({
  state,
  borrowerType,
  interestOnly,
  lvr,
  rateDisplayType,
}: MortgageFilterPreferencesProps) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  // Save current selections to localStorage ONLY if URL has search parameters
  useEffect(() => {
    if (typeof window !== "undefined" && searchParams.size > 0) {
      console.debug("Saving current selection from URL params")
      localStorage.setItem("mortgage_state", state)
      localStorage.setItem("mortgage_borrowerType", borrowerType)
      localStorage.setItem("mortgage_interestOnly", String(interestOnly))
      localStorage.setItem("mortgage_lvr", lvr)
      localStorage.setItem("mortgage_rateDisplayType", rateDisplayType)
    }
  }, [state, borrowerType, interestOnly, lvr, rateDisplayType, searchParams])

  // Check if we need to restore preferences
  useEffect(() => {
    // Skip on server
    if (typeof window === "undefined") return

    // Get current URL parameters
    const params = new URLSearchParams(window.location.search)

    // If we have params, use them instead of local storage
    if (params.size) {
      return
    }

    // No params, use stored preferences if any
    const storedState = localStorage.getItem("mortgage_state")
    const storedBorrowerType = localStorage.getItem("mortgage_borrowerType")
    const storedInterestOnly = localStorage.getItem("mortgage_interestOnly")
    const storedLvr = localStorage.getItem("mortgage_lvr")
    const storedRateDisplayType = localStorage.getItem("mortgage_rateDisplayType")

    // Check if any stored preference is different from current URL params
    // or if params are at default values
    const needsRedirect = storedState || storedBorrowerType || storedInterestOnly || storedLvr || storedRateDisplayType

    // Only redirect if we have stored values that differ from current URL
    if (needsRedirect) {
      console.debug("Redirect needed", storedState)
      const newParams = new URLSearchParams()

      // Use stored values if available, otherwise use current props as fallback
      newParams.set("state", storedState || state)
      newParams.set("borrowerType", storedBorrowerType || borrowerType)
      newParams.set("interestOnly", storedInterestOnly || String(interestOnly))
      newParams.set("lvr", storedLvr || lvr)
      newParams.set("rateDisplayType", storedRateDisplayType || rateDisplayType)

      router.replace(`${pathname}?${newParams.toString()}`)
    }
  }, [pathname, router, state, borrowerType, interestOnly, lvr, rateDisplayType]) // Include props as dependencies

  // This component doesn't render anything visible
  return null
}
